import { useRef, useCallback, useEffect, useMemo } from "react";

export default function useAreaChatWindowUI({
  messages,
  pagination,
  containerRef
}) {
  /**
   * Ref al marcador final de mensajes para scroll automático.
   * @type {React.RefObject}
   * @memberof useAreaChatWindowUI
   */
  const messagesEndRef = useRef(null);

  /**
   * Handler para hacer scroll instantáneo al final del chat.
   *
   * @function scrollToBottom
   * @memberof useAreaChatWindowUI
   * @returns {void}
   *
   * @description
   * - Hace scroll al final del contenedor y enfoca el marcador final.
   * - Usado tras carga inicial o llegada de nuevo mensaje.
   *
   * @example
   * scrollToBottom();
   */
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, []);

  /**
   * Ref para guardar el estado previo de los mensajes entre renders.
   * @type {React.MutableRefObject<Array>}
   * @private
   * @memberof useAreaChatWindowUI
   */
  const prevMessagesRef = useRef([]);

  /**
   * Efecto optimizado para scroll automático solo en primera carga o mensaje nuevo al final.
   *
   * @function useEffectScroll
   * @memberof useAreaChatWindowUI
   * @private
   *
   * @description
   * - Detecta si es la primera página o si hay un mensaje nuevo al final.
   * - Ejecuta scrollToBottom solo en esos casos para evitar scroll innecesario.
   * - Limpia el timer correctamente para evitar fugas de memoria.
   */
  useEffect(() => {
    const isFirstPage = pagination.current_page === 1;
    const prevMessages = prevMessagesRef.current;
    const prevLength = prevMessages.length;
    const isNewMessage =
      messages.length > prevLength &&
      (
        prevLength === 0 ||
        (messages[messages.length - 1]?.id !== prevMessages[prevLength - 1]?.id)
      );

    if (isFirstPage || isNewMessage) {
      const timer = setTimeout(scrollToBottom, 0);
      prevMessagesRef.current = messages;
      return () => clearTimeout(timer);
    }
    // Actualiza el ref después de la comprobación
    prevMessagesRef.current = messages;
    return undefined;
  }, [messages, scrollToBottom, pagination.current_page]);

  /**
   * Devuelve objeto memoizado con refs y handlers visuales para el área de chat.
   *
   * @returns {AreaChatWindowUIResult} Objeto con refs y handlers visuales
   *
   * @example
   * const { messagesEndRef, scrollToBottom } = useAreaChatWindowUI(...);
   */
  return useMemo(() => ({
    messagesEndRef,
    scrollToBottom,
  }), [messagesEndRef, scrollToBottom ]);
}
